/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SwipeableEdgeDrawer from '../swipable-edge-drawer/swipable-edge-drawer.component';
import LocationsSearch from '../locations-search/locations-search.component';
import { useMediaQuery } from '@mui/material';
import { locationsListStyles } from './locations-list.styles';
import { useSelector } from 'react-redux';
import { selectGeoLocation } from '../../store/geo-location/geo-location.selector';
import { useIntl } from 'react-intl';
import { LocationItem } from '../../app/main/components/location/location-item.component';
import { useNavigate } from 'react-router-dom';
import FiltersBlock from '../../app/main/components/filters/filters-block.component';
import { LocationItemsSkeleton } from '../../app/main/components/location/location-items-skeleton.component';
import { useLeafletMap } from '../leaflet-map/MapContext';
import { LatLngExpression } from 'leaflet';
import { ILocation, IFilterLocations } from '../../api/locations/types';
import { EStationStatus } from '../../api/station/types';
import { useGetLocationsQuery } from '../../api/locations/locations.api';
import { selectUnitsOfDistance } from '../../store/app/app.selector';
import { POLLING_INTERVAL_MS } from '../../utils/station-cache';
import { EUnitsOfDistance } from '../../types';

const LocationsList = () => {
    const isDesktop = useMediaQuery('(min-width:600px)');
    const locationsListParams = useSelector((state: any) => state.locationsListParams);
    const { entry: location } = useSelector(selectGeoLocation);
    const [isFiltersChanged, setIsFiltersChanged] = useState(false);
    const navigate = useNavigate();
    const intl = useIntl();
    const { map, zoomToLocation } = useLeafletMap();
    const units = useSelector(selectUnitsOfDistance);

    const filteredParams = useMemo(() => {
        const filteredParams = Object.fromEntries(
            Object.entries(locationsListParams || {}).filter(([_, value]) => value !== undefined)
        );
        const {
            isFree,
            isPaid,
            canadianMadeOnly,
            connectorStatus,
            isConnectorAvailable,
            ...restParams
        } = filteredParams;

        return {
            ...restParams,
            ...(location.lng ? { lng: location.lng } : {}),
            ...(location.lat ? { lat: location.lat } : {}),
            reload: true,
            cursor: 0,
            isFree: isFree ? true : undefined,
            isPaid: isPaid ? true : undefined,
            canadianMadeOnly: canadianMadeOnly ? true : undefined,
            connectorStatus: isConnectorAvailable
                ? EStationStatus.AVAILABLE
                : connectorStatus
                  ? (connectorStatus as EStationStatus)
                  : undefined,
        } as IFilterLocations;
    }, [locationsListParams, location.lng, location.lat]);

    const {
        data: locations,
        isLoading: isLocationsLoading,
        error: locationsError,
        isFetching: isLocationsFetching,
    } = useGetLocationsQuery(filteredParams, {
        refetchOnMountOrArgChange: true,
        pollingInterval: POLLING_INTERVAL_MS,
        skipPollingIfUnfocused: true,
    });

    useEffect(() => {
        setIsFiltersChanged(true);
    }, [filteredParams]);

    useEffect(() => {
        if (!isLocationsFetching && isFiltersChanged) {
            setIsFiltersChanged(false);
        }
    }, [isLocationsFetching, isFiltersChanged]);

    const handleClick = useCallback((locationId: string) => {
        navigate(`/locations/${locationId}${window.location.search}`);
    }, []);

    const zoomToCommercialLocation = useCallback(
        (commercialLocation: ILocation) => {
            zoomToLocation(
                commercialLocation.location?.coordinates?.toReversed() as LatLngExpression
            );
        },
        [map]
    );

    const mappedLocations = useMemo(() => {
        const rows = locations?.data || [];

        if ((isLocationsLoading || isLocationsFetching) && (!rows.length || isFiltersChanged)) {
            return (
                <div css={locationsListStyles.skeletonLoadingBlock}>
                    <LocationItemsSkeleton />
                </div>
            );
        }

        if (locationsError) {
            return null;
        }

        if (!rows.length) {
            return (
                <div css={locationsListStyles.loadingBlock}>
                    {intl.formatMessage({ id: 'noResultsFound' })}
                </div>
            );
        }
        return (
            <div css={locationsListStyles.list}>
                {rows.map(location => (
                    <LocationItem
                        key={location._id}
                        location={location}
                        onClick={() => {
                            handleClick(location._id);
                            zoomToCommercialLocation(location);
                        }}
                        isMiles={units === EUnitsOfDistance.MILES}
                    />
                ))}
            </div>
        );
    }, [
        locations,
        isLocationsLoading,
        isLocationsFetching,
        zoomToCommercialLocation,
        isFiltersChanged,
    ]);

    const content = useMemo(() => {
        return (
            <div css={locationsListStyles.root}>
                <LocationsSearch />
                <FiltersBlock />
                {mappedLocations}
            </div>
        );
    }, [mappedLocations]);

    if (isDesktop) {
        return <div css={locationsListStyles.desktopWrapper}>{content}</div>;
    }

    return (
        <SwipeableEdgeDrawer keepMounted hideBackdrop drawerBleeding={200}>
            {content}
        </SwipeableEdgeDrawer>
    );
};

export default LocationsList;
